import moment from 'jalali-moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import endpoints from '../../config/endpoints';
import globalEnum from '../../enums/globalEnum';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';
import { DATE_ALL, DateRange, DateRangePresets, toToman } from './AnalyticsLayout';
import { Sidebar } from '../Layout/Sidebar';
import { USER_TYPE_LABELS } from '../shared/UserPicker';

const STATUS_LABELS = globalEnum.orderStatus2;
const COLORS = ['#1A6BFF', '#4D8FFF', '#7A8CA0', '#3D4F6B', '#0F1923', '#1254D6', '#93C5FD', '#64748B'];

function statusClass(s: string) {
  if (s === 'Sent') return 'statusSent';
  if (s === 'Canceled') return 'statusCanceled';
  if (s.includes('Wait') || s.includes('Progress')) return 'statusWaiting';
  if (s === 'Created') return 'statusCreated';
  return 'statusOther';
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useDispatch();

  const passedRange: DateRange | undefined = (location.state as any)?.dateRange;
  const [dateRange, setDateRange] = useState<DateRange>(passedRange || { from: DATE_ALL, to: DATE_ALL });
  const [data, setData] = useState<any>(null);

  const fetchData = useCallback(async () => {
    if (!id) return;
    dispatch(setLoading(true));
    const apiQuery = dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : {};
    const res = await restApi(endpoints.analytics.userDetail + id).get(apiQuery);
    if (res.code === 200) setData(res.data);
    dispatch(setLoading(false));
  }, [id, dateRange, dispatch]);

  // Presets apply immediately (no separate "apply" button) — refetch
  // whenever the resolved date range changes.
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportOrders = () => {
    if (!data?.orders) return;
    const rows = data.orders.map((o: any) => ({
      'کد سفارش': o.code || o.id,
      'وضعیت': STATUS_LABELS[o.status] || o.status,
      'مبلغ نهایی (تومان)': toToman(o.finalPrice),
      'تاریخ ثبت': o.placedDate ? moment(o.placedDate).format('jYYYY/jMM/jDD') : '—',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'سفارشات');
    XLSX.writeFile(wb, `user-${id}-orders.xlsx`);
  };

  const exportNeverBought = () => {
    if (!data?.neverBought) return;
    const rows = data.neverBought.map((p: any) => ({ 'کد': p.sku, 'عنوان': p.title }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'نخریده‌ها');
    XLSX.writeFile(wb, `user-${id}-never-bought.xlsx`);
  };

  return (
    <body className="dashboardBody">
      <Sidebar />
      <main className="analyticsMain">

        {/* Back + filter bar */}
        <div style={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Link to="/analytics/users" className="analyticsBackBtn">← بازگشت به کاربران</Link>
          <DateRangePresets dateRange={dateRange} onChange={setDateRange} />
        </div>

        {/* User card */}
        {data?.user && (
          <div className="analyticsUserCard">
            <div className="analyticsUserAvatar">👤</div>
            <div className="analyticsUserInfo">
              <div className="analyticsUserName">{data.user.name || data.user.phoneNumber}</div>
              <div className="analyticsUserMeta">
                <span>📱 {data.user.phoneNumber}</span>
                {data.user.companyName && <span>🏢 {data.user.companyName}</span>}
                {data.user.email && <span>✉️ {data.user.email}</span>}
                <span>نوع: {USER_TYPE_LABELS[data.user.userType] || 'نامشخص'}</span>
                {data.user.createdAt && <span>عضویت: {moment(data.user.createdAt).format('jYYYY/jMM/jDD')}</span>}
              </div>
            </div>
          </div>
        )}

        {/* KPI */}
        <div className="analyticsKpiRow">
          <div className="analyticsKpiCard">
            <div className="analyticsKpiIcon kpiGreen">💰</div>
            <div className="analyticsKpiValue">{tools.formatPrice(toToman(data?.totalIncome))} تومان</div>
            <div className="analyticsKpiLabel">مجموع خرید (ارسال‌شده)</div>
          </div>
          <div className="analyticsKpiCard">
            <div className="analyticsKpiIcon kpiBlue">📦</div>
            <div className="analyticsKpiValue">{data?.totalOrders ?? 0}</div>
            <div className="analyticsKpiLabel">تعداد سفارشات در بازه</div>
          </div>
        </div>

        {/* Income by month chart */}
        {(data?.incomeByMonth?.length ?? 0) > 0 && (
          <div className="analyticsChartCard" style={{ marginBottom: 20 }}>
            <div className="analyticsChartTitle">درآمد ماهانه از این کاربر</div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.incomeByMonth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="userIncomeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1A6BFF" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#1A6BFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fontFamily: 'Yekan Bakh' }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => tools.formatPrice(toToman(v))} width={90} />
                <Tooltip formatter={(v: any) => [tools.formatPrice(toToman(v)) + ' تومان', 'درآمد']} />
                <Area type="monotone" dataKey="income" stroke="#1A6BFF" fill="url(#userIncomeGrad)" strokeWidth={2} dot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Charts row */}
        <div className="analyticsChartGrid">
          {/* Top products chart */}
          {(data?.topProducts?.length ?? 0) > 0 && (
            <div className="analyticsChartCard">
              <div className="analyticsChartTitle">پرخریدترین محصولات (عدد)</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.topProducts.map((p: any) => ({ ...p, name: p.sku || p.title }))} layout="vertical" margin={{ right: 16, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fontFamily: 'Yekan Bakh', direction: 'ltr' }} />
                  <Tooltip formatter={(v: any) => [v, 'عدد']} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {(data.topProducts || []).map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Orders table */}
        <div className="analyticsTableCard">
          <div className="analyticsTableHeader">
            <div className="analyticsChartTitle" style={{ margin: 0 }}>سفارشات در بازه زمانی</div>
            <button className="analyticsExportBtn" onClick={exportOrders}>⬇ خروجی اکسل</button>
          </div>
          <table className="alternating-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>کد سفارش</th><th>تاریخ</th><th>وضعیت</th><th>مبلغ (تومان)</th><th>فاکتور</th></tr>
            </thead>
            <tbody>
              {(data?.orders || []).map((o: any, i: number) => (
                <tr key={o.id} className={i % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                  <td>{o.code || o.id}</td>
                  <td>{o.placedDate ? moment(o.placedDate).format('jYYYY/jMM/jDD') : '—'}</td>
                  <td><span className={`statusBadge ${statusClass(o.status)}`}>{STATUS_LABELS[o.status] || o.status}</span></td>
                  <td>{tools.formatPrice(toToman(o.finalPrice))}</td>
                  <td><Link to={`/order/invoice/${o.id}`} target="_blank">فاکتور</Link></td>
                </tr>
              ))}
              {(!data?.orders || data.orders.length === 0) && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: '#aaa' }}>سفارشی یافت نشد</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Never bought */}
        <div className="analyticsTableCard">
          <div className="analyticsTableHeader">
            <div className="analyticsChartTitle" style={{ margin: 0 }}>محصولاتی که هرگز خریداری نشده</div>
            <button className="analyticsExportBtn" onClick={exportNeverBought}>⬇ خروجی اکسل</button>
          </div>
          <table className="alternating-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>#</th><th>کد محصول (SKU)</th><th>عنوان محصول</th></tr>
            </thead>
            <tbody>
              {(data?.neverBought || []).map((p: any, i: number) => (
                <tr key={p.productId} className={i % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                  <td>{i + 1}</td>
                  <td style={{ direction: 'ltr' }}>{p.sku || '—'}</td>
                  <td>{p.title || '—'}</td>
                </tr>
              ))}
              {(!data?.neverBought || data.neverBought.length === 0) && (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: 30, color: '#aaa' }}>—</td></tr>
              )}
            </tbody>
          </table>
        </div>

      </main>
    </body>
  );
}
